import { useState } from 'react';

import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { TemplateCodeEditor } from '../TemplateCodeEditor';
import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { CallApiNodeData } from '@repo/shared-types';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
const BODY_METHODS = new Set(['POST', 'PUT', 'PATCH']);

const SAMPLE_VAR_VALUES: Record<string, string> = {
  'call.id': 'call-abc-123',
  'call.direction': 'INBOUND',
  'call.channel': 'voice',
  'call.modality': 'VOICE',
  'call.outcome': 'LEAD_CAPTURED',
  'call.duration': '120',
  'call.phone_number': '+44 7700 900000',
  'contact.name': 'John Doe',
  'contact.phone': '+44 7700 900000',
  'campaign.name': 'Q1 Outreach',
  'campaign.id': 'camp-123',
  customer_name: 'John Doe',
  phone_number: '+44 7700 900000',
  vehicle_interest: '2024 BMW 3 Series',
};

function resolveVariables(template: string): string {
  return template.replace(/\{\{([^}]+)\}\}/g, (_match, key: string) => {
    const trimmed = key.trim();
    return SAMPLE_VAR_VALUES[trimmed] ?? `[${trimmed}]`;
  });
}

function extractFieldPaths(obj: unknown, prefix = ''): Record<string, unknown> {
  if (typeof obj !== 'object' || obj === null) return {};
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    result[path] = val;
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      Object.assign(result, extractFieldPaths(val, path));
    }
    if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') {
      Object.assign(result, extractFieldPaths(val[0], path));
    }
  }
  return result;
}

interface TestResponse {
  status: number;
  ok: boolean;
  body: unknown;
}

interface CallApiConfigProps {
  data: CallApiNodeData & { sampleResponseSchema?: Record<string, unknown> };
  onChange: (data: CallApiNodeData & { sampleResponseSchema?: Record<string, unknown> }) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

export const CallApiConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: CallApiConfigProps) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResponse, setTestResponse] = useState<TestResponse | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [responseExpanded, setResponseExpanded] = useState(false);

  const update = (
    patch: Partial<CallApiNodeData & { sampleResponseSchema?: Record<string, unknown> }>,
  ) => onChange({ ...data, ...patch });

  const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
    const updated = data.headers.map((h, i) => (i === index ? { ...h, [field]: value } : h));
    update({ headers: updated });
  };

  const addHeader = () => {
    update({ headers: [...data.headers, { key: '', value: '' }] });
  };

  const removeHeader = (index: number) => {
    update({ headers: data.headers.filter((_, i) => i !== index) });
  };

  const handleTestApi = async () => {
    setIsTesting(true);
    setTestResponse(null);
    setTestError(null);
    setResponseExpanded(true);

    try {
      const resolvedUrl = resolveVariables(data.url);
      const resolvedBody =
        BODY_METHODS.has(data.method) && data.body ? resolveVariables(data.body) : undefined;

      const headers: Record<string, string> = {};
      for (const h of data.headers) {
        if (h.key.trim()) {
          headers[h.key.trim()] = resolveVariables(h.value);
        }
      }
      if (resolvedBody && !headers['Content-Type'] && !headers['content-type']) {
        headers['Content-Type'] = 'application/json';
      }

      const res = await fetch(resolvedUrl, {
        method: data.method,
        headers,
        body: resolvedBody,
      });

      let body: unknown;
      const contentType = res.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        body = await res.json();
      } else {
        body = await res.text();
      }

      setTestResponse({ status: res.status, ok: res.ok, body });

      if (res.ok && typeof body === 'object' && body !== null) {
        const schema = extractFieldPaths(body);
        update({ sampleResponseSchema: schema });
      }
    } catch (err) {
      setTestError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <Input
          value={data.label}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Node label"
        />
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Method</label>
        <select
          value={data.method}
          onChange={(e) => update({ method: e.target.value as CallApiNodeData['method'] })}
          className="border-input bg-background ring-offset-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          {HTTP_METHODS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">URL</label>
        <div className="flex gap-1">
          <VariableAutocomplete
            value={data.url}
            onChange={(v) => update({ url: v })}
            placeholder="https://api.example.com/endpoint"
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            className="flex-1"
          />
          <VariablePicker
            onSelect={(v) => update({ url: data.url + v })}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="text-muted-foreground text-xs font-medium">Headers</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={addHeader}
          >
            + Add
          </Button>
        </div>
        <div className="space-y-2">
          {data.headers.map((header, index) => (
            <div key={index} className="flex gap-1">
              <Input
                value={header.key}
                onChange={(e) => updateHeader(index, 'key', e.target.value)}
                placeholder="Key"
                className="flex-1"
              />
              <VariableAutocomplete
                value={header.value}
                onChange={(v) => updateHeader(index, 'value', v)}
                placeholder="Value"
                responseFieldsSchema={responseFieldsSchema}
                reviewNodeMetas={reviewNodeMetas}
                classifierNodeMetas={classifierNodeMetas}
                qualityNodeMetas={qualityNodeMetas}
                agentNodeMetas={agentNodeMetas}
                className="flex-1"
              />
              <VariablePicker
                onSelect={(v) => updateHeader(index, 'value', header.value + v)}
                responseFieldsSchema={responseFieldsSchema}
                reviewNodeMetas={reviewNodeMetas}
                classifierNodeMetas={classifierNodeMetas}
                qualityNodeMetas={qualityNodeMetas}
                agentNodeMetas={agentNodeMetas}
                triggerType={triggerType}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs text-red-500 hover:text-red-600"
                onClick={() => removeHeader(index)}
              >
                ×
              </Button>
            </div>
          ))}
        </div>
      </div>

      {BODY_METHODS.has(data.method) && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-muted-foreground text-xs font-medium">Body</label>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setEditorOpen(true)}
            >
              Edit Body
            </Button>
          </div>
          {data.body ? (
            <div
              className="border-input bg-muted/30 max-h-32 cursor-pointer overflow-hidden rounded-md border p-2 text-xs"
              onClick={() => setEditorOpen(true)}
            >
              <p className="text-muted-foreground line-clamp-5 font-mono">{data.body}</p>
            </div>
          ) : (
            <div
              className="border-input text-muted-foreground cursor-pointer rounded-md border border-dashed p-4 text-center text-xs"
              onClick={() => setEditorOpen(true)}
            >
              Click to edit JSON body...
            </div>
          )}
        </div>
      )}

      <TemplateCodeEditor
        value={data.body ?? ''}
        onChange={(v) => update({ body: v })}
        type="json"
        reviewSchema={responseFieldsSchema}
        reviewNodeMetas={reviewNodeMetas}
        classifierNodeMetas={classifierNodeMetas}
        qualityNodeMetas={qualityNodeMetas}
        agentNodeMetas={agentNodeMetas}
        analyticsNodeMetas={analyticsNodeMetas}
        open={editorOpen}
        onOpenChange={setEditorOpen}
      />

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Output Key</label>
        <Input
          value={data.outputKey}
          onChange={(e) => update({ outputKey: e.target.value })}
          placeholder="api_response"
        />
      </div>

      {/* Test API */}
      <div className="space-y-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full text-xs"
          onClick={() => void handleTestApi()}
          disabled={isTesting || !data.url}
        >
          {isTesting ? (
            <>
              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              Testing...
            </>
          ) : (
            'Test API'
          )}
        </Button>

        {testError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            {testError}
          </div>
        )}

        {testResponse && (
          <div className="border-input rounded-md border text-xs">
            <button
              type="button"
              className="flex w-full items-center gap-1.5 px-3 py-2 text-left font-medium"
              onClick={() => setResponseExpanded((v) => !v)}
            >
              {responseExpanded ? (
                <ChevronDown className="h-3 w-3 shrink-0" />
              ) : (
                <ChevronRight className="h-3 w-3 shrink-0" />
              )}
              <span
                className={
                  testResponse.ok
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }
              >
                {testResponse.status}
              </span>
              <span className="text-muted-foreground">{testResponse.ok ? 'Success' : 'Error'}</span>
              {testResponse.ok && data.sampleResponseSchema && (
                <span className="text-muted-foreground ml-auto font-normal">
                  {Object.keys(data.sampleResponseSchema).length} fields captured
                </span>
              )}
            </button>
            {responseExpanded && (
              <div className="border-input border-t">
                <pre className="bg-muted/30 max-h-64 overflow-auto rounded-b-md p-3 font-mono text-[11px] leading-relaxed">
                  {typeof testResponse.body === 'string'
                    ? testResponse.body
                    : JSON.stringify(testResponse.body, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
